import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { Avatar } from '../components/DetailViews';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const chartStyle = { fontSize: 10, fontFamily: 'Share Tech Mono', fill: 'var(--txt2)' };

export default function Dashboard() {
  const { user, can } = useAuth();
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [pods, setPods] = useState([]);
  const [quests, setQuests] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/stats'), api.get('/users/leaderboard'),
      api.get('/announcements'), api.get('/pods'), api.get('/quests'),
      api.get('/analytics/me'),
    ]).then(([s, lb, ann, p, q, an]) => {
      setStats(s.data); setLeaderboard(lb.data.slice(0, 5));
      setAnnouncements(ann.data.slice(0, 3)); setPods(p.data); setQuests(q.data);
      setAnalytics(an.data);
    }).catch(() => {});
  }, []);

  const pointsHistory = (analytics?.weeklyPts || []).map(w => ({ week: w.label, points: w.value }));

  const tierColors = { Probation: '#4fc3f7', Core: '#00ff88', Veteran: '#ffd700', Legend: '#ff6b35' };
  const ragColor = { green: 'var(--j5)', amber: 'var(--j4)', red: 'var(--j6)' };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div className="page-title">COMMAND CENTRE</div>
        <div className="page-sub">NeuralOps AI COE · FY 2026–27 Pilot · 90-Day Window</div>
      </div>

      {/* Welcome banner */}
      <div className="card" style={{ marginBottom: 20, background: 'linear-gradient(135deg, rgba(0,212,255,0.06), rgba(0,153,255,0.03))' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontFamily: 'Orbitron', fontSize: 16, fontWeight: 700, color: 'var(--txt)', marginBottom: 4 }}>Welcome back, {user?.name?.split(' ')[0]} 👋</div>
            <div style={{ fontSize: 12, color: 'var(--txt2)', fontFamily: 'Share Tech Mono', letterSpacing: 1 }}>
              Tier: <span style={{ color: tierColors[user?.tier] }}>{user?.tier}</span> · Monthly floor: {user?.monthlyFloor} pts · Kill/Scale Gate: 09 Sep 2026
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontFamily: 'Orbitron', fontSize: 32, fontWeight: 800, color: 'var(--j1)', textShadow: '0 0 20px var(--glow1)' }}>{(user?.totalPoints || 0).toLocaleString()}</div>
            <div style={{ fontSize: 9, color: 'var(--txt2)', fontFamily: 'Share Tech Mono', letterSpacing: 2 }}>TOTAL POINTS</div>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid-4" style={{ marginBottom: 20 }}>
        {[
          { val: stats?.totalMembers || '—', label: 'Active Members' },
          { val: stats?.activeQuests || '—', label: 'Open Quests' },
          { val: stats?.activePods || '—', label: 'Active Pods' },
          { val: stats?.libraryEntries || '—', label: 'Library Entries' },
        ].map((s, i) => (
          <div key={i} className="card">
            <div className="stat-val">{s.val}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        {/* My points trend */}
        <div className="card">
          <div className="section-header">
            <div className="section-title">MY POINTS TREND</div>
            <span style={{ fontSize: 10, color: 'var(--j4)', fontFamily: 'Share Tech Mono' }}>Weekly</span>
          </div>
          <ResponsiveContainer width="100%" height={140}>
            <LineChart data={pointsHistory}>
              <XAxis dataKey="week" tick={chartStyle} axisLine={false} tickLine={false} />
              <YAxis tick={chartStyle} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: 'var(--bg3)', border: '1px solid var(--brd2)', borderRadius: 3, fontSize: 11, fontFamily: 'Share Tech Mono' }} />
              <Line type="monotone" dataKey="points" stroke="var(--j1)" strokeWidth={2} dot={{ fill: 'var(--j1)', r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Top contributors */}
        <div className="card">
          <div className="section-header">
            <div className="section-title">TOP CONTRIBUTORS</div>
            <a href="/app/leaderboard" style={{ fontSize: 10, color: 'var(--j1)', fontFamily: 'Share Tech Mono', textDecoration: 'none' }}>VIEW ALL →</a>
          </div>
          {leaderboard.map((m, i) => (
            <div key={m.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--brd2)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontFamily: 'Orbitron', fontSize: 12, fontWeight: 700, color: i === 0 ? 'var(--j4)' : 'var(--txt3)', width: 20 }}>#{i + 1}</span>
                <Avatar user={m} size={24} />
                <div>
                  <div style={{ fontSize: 12, color: 'var(--txt)', fontWeight: 600 }}>{m.name}</div>
                  <span className={`tier-badge tier-${m.tier?.toLowerCase()}`} style={{ fontSize: 8 }}>{m.tier}</span>
                </div>
              </div>
              <div style={{ fontFamily: 'Orbitron', fontSize: 13, fontWeight: 700, color: 'var(--j1)' }}>{(m.totalPoints || 0).toLocaleString()}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Active Pods */}
        <div className="card">
          <div className="section-header"><div className="section-title">ACTIVE PODS</div></div>
          {pods.filter(p => p.status === 'active').map(pod => (
            <div key={pod.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--brd2)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--txt)' }}>{pod.name}</div>
                <span style={{ color: ragColor[pod.ragStatus], fontSize: 10, fontFamily: 'Share Tech Mono' }}>● {pod.ragStatus?.toUpperCase()}</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--txt2)', fontFamily: 'Share Tech Mono', letterSpacing: 0.5 }}>
                Lead: {pod.lead} · SME: {pod.sme}
              </div>
              <div style={{ marginTop: 6, background: 'var(--bg4)', borderRadius: 2, height: 4 }}>
                <div style={{ width: `${Math.round((pod.currentMilestone / pod.milestones?.length) * 100)}%`, height: 4, borderRadius: 2, background: 'linear-gradient(90deg, var(--j2), var(--j1))' }} />
              </div>
              <div style={{ fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', marginTop: 3 }}>Milestone {pod.currentMilestone}/{pod.milestones?.length}</div>
            </div>
          ))}
          {pods.filter(p => p.status === 'active').length === 0 && <div className="empty-state">No active pods</div>}
        </div>

        {/* Announcements */}
        <div className="card">
          <div className="section-header"><div className="section-title">ANNOUNCEMENTS</div></div>
          {announcements.map(ann => (
            <div key={ann.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--brd2)' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--txt)', marginBottom: 4 }}>{ann.title}</div>
              <div style={{ fontSize: 11, color: 'var(--txt2)', lineHeight: 1.5 }}>{ann.content}</div>
              <div style={{ fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', marginTop: 4 }}>{new Date(ann.createdAt).toLocaleDateString()} · {ann.author}</div>
            </div>
          ))}
          {announcements.length === 0 && <div className="empty-state">No announcements yet</div>}
        </div>
      </div>
    </div>
  );
}
